/*
 * @Author: Ryan
 * @Date: 2026-01-21
 * @Description: 类型下拉选择组件
 */
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { GeneratorOption } from './config';
import styles from './index.module.scss';

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
    <path
      d="M11.2 3.6L5.6 9.8L2.8 7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

interface TypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  options: GeneratorOption[];
}

const TypeSelector: FC<TypeSelectorProps> = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const currentOption = useMemo(() => {
    return options.find((opt) => opt.key === value) || options[0];
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const handleSelect = (key: string) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <div className={styles.typeSelectorWrapper} ref={wrapperRef}>
      <button
        type="button"
        className={`${styles.typeSelector} ${open ? styles.typeSelectorOpen : ''}`}
        onClick={() => setOpen(!open)}
      >
        <span>{currentOption.label}</span>
        <span className={styles.typeSelectorArrow}>
          <ChevronDownIcon />
        </span>
      </button>

      {open && (
        <div className={styles.dropdownMenu}>
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`${styles.dropdownItem} ${opt.key === value ? styles.dropdownItemActive : ''}`}
              onClick={() => handleSelect(opt.key)}
            >
              <span>{opt.label}</span>
              {opt.key === value && (
                <span className={styles.dropdownItemCheck}>
                  <CheckIcon />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TypeSelector;
